import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { Role, RoleInput, UserKind } from '@/api/accessApi'
import { FormError } from '@/components/admin/AdminShared'
import { Field } from '@/components/common/Field'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { usePermissionCatalog } from '@/hooks/useAccess'
import { useCustomers, useProducts } from '@/hooks/useDirectory'
import {
  canGrant,
  emptyRoleForm,
  groupState,
  groupsForKind,
  rolePayload,
  roleToForm,
  slugFromName,
  toggleGroup,
  togglePermission,
  validateRole,
  type RoleForm,
  type RoleFormErrors,
} from '@/lib/accessForms'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'

const KINDS: UserKind[] = ['AGENT', 'CUSTOMER']

/** Create, edit or (built-in) view a role: name, permission matrix by group, and scope. */
export function RoleDialog({
  open,
  role,
  pending,
  error,
  onOpenChange,
  onSubmit,
}: {
  open: boolean
  role: Role | null
  pending: boolean
  error: unknown
  onOpenChange: (open: boolean) => void
  onSubmit: (input: RoleInput) => void
}) {
  const { t } = useTranslation()
  const title = !role
    ? t('access.roles.createTitle')
    : role.isSystem
      ? t('access.roles.viewTitle', { name: role.displayName })
      : t('access.roles.editTitle', { name: role.displayName })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {open ? <RoleFormBody key={role?.id ?? 'new'} role={role} pending={pending} error={error} onCancel={() => onOpenChange(false)} onSubmit={onSubmit} /> : null}
      </DialogContent>
    </Dialog>
  )
}

function RoleFormBody({
  role,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  role: Role | null
  pending: boolean
  error: unknown
  onCancel: () => void
  onSubmit: (input: RoleInput) => void
}) {
  const { t } = useTranslation()
  const { user: me } = useAuth()
  const editing = role !== null
  const readOnly = role?.isSystem ?? false
  const [form, setForm] = useState<RoleForm>(() => (role ? roleToForm(role) : emptyRoleForm()))
  const [slugTouched, setSlugTouched] = useState(editing)
  const [errors, setErrors] = useState<RoleFormErrors>({})
  const catalog = usePermissionCatalog()
  const products = useProducts(form.kind === 'AGENT')
  const customers = useCustomers(form.kind === 'AGENT')
  const mine = { isSuperAdmin: me?.isSuperAdmin ?? false, permissions: me?.permissions ?? [] }
  const groups = groupsForKind(catalog.data ?? [], form.kind)
  const set = <K extends keyof RoleForm>(key: K, value: RoleForm[K]) => setForm((f) => ({ ...f, [key]: value }))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const found = validateRole(form, editing)
    setErrors(found)
    if (Object.keys(found).length === 0) onSubmit(rolePayload(form, editing))
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
      {readOnly ? <p className="rounded-md bg-surface-subtle px-3 py-2 text-meta text-ink-body">{t('access.roles.readOnly')}</p> : null}

      <div className="grid grid-cols-2 gap-4">
        <Field id="r-name" label={t('access.roles.fields.name')} error={errors.displayName ? t(errors.displayName) : null}>
          <Input
            id="r-name"
            value={form.displayName}
            disabled={readOnly}
            onChange={(e) => {
              const displayName = e.target.value
              setForm((f) => ({ ...f, displayName, slug: slugTouched ? f.slug : slugFromName(displayName) }))
            }}
          />
        </Field>
        <Field id="r-slug" label={t('access.roles.fields.slug')} error={errors.slug ? t(errors.slug) : null} help={editing ? undefined : t('access.roles.fields.slugHelp')}>
          <Input
            id="r-slug"
            className="font-mono"
            value={form.slug}
            disabled={editing}
            onChange={(e) => {
              setSlugTouched(true)
              set('slug', e.target.value)
            }}
          />
        </Field>
      </div>

      {!editing ? (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="pb-1.5 text-meta font-medium text-ink">{t('access.roles.fields.kind')}</legend>
          <div role="radiogroup" className="inline-flex w-fit rounded-md border border-line-strong p-0.5">
            {KINDS.map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={form.kind === k}
                // Switching kind drops what the other kind may not hold.
                onClick={() => setForm((f) => ({ ...emptyRoleForm(k), displayName: f.displayName, slug: f.slug }))}
                className={cn(
                  'rounded-[5px] px-3 py-1.5 text-meta focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  form.kind === k ? 'bg-accent font-medium text-accent-foreground' : 'text-ink-muted hover:text-ink',
                )}
              >
                {t(`access.kindsPlural.${k}`)}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}

      {form.kind === 'CUSTOMER' ? (
        <label className="flex items-center gap-2 text-meta text-ink">
          <input type="checkbox" className="size-4 accent-primary" disabled={readOnly} checked={form.customerWide} onChange={(e) => set('customerWide', e.target.checked)} />
          {t('access.roles.fields.customerWide')}
        </label>
      ) : null}

      <section className="flex flex-col gap-2" aria-label={t('access.roles.fields.permissions')}>
        <p className="text-meta font-medium text-ink">{t('access.roles.fields.permissions')}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {groups.map((g) => {
            const state = groupState(form.permissions, g)
            const grantable = g.permissions.filter((p) => canGrant(mine, [p.key]))
            return (
              <fieldset key={g.group} className="rounded-lg border border-line p-3">
                <legend className="px-1">
                  <GroupCheckbox
                    label={g.group}
                    ariaLabel={t('access.roles.selectGroup', { group: g.group })}
                    state={state}
                    disabled={readOnly || grantable.length !== g.permissions.length}
                    onChange={(on) => set('permissions', toggleGroup(form.permissions, g, on))}
                  />
                </legend>
                <ul className="flex flex-col gap-1.5 pt-1">
                  {g.permissions.map((p) => {
                    const allowed = canGrant(mine, [p.key])
                    return (
                      <li key={p.key}>
                        <label className={cn('flex items-start gap-2 text-meta', allowed || readOnly ? 'text-ink-body' : 'text-ink-placeholder')} title={allowed ? undefined : t('access.roles.fields.notGrantable')}>
                          <input
                            type="checkbox"
                            className="mt-0.5 size-4 shrink-0 accent-primary"
                            checked={form.permissions.includes(p.key)}
                            disabled={readOnly || !allowed}
                            onChange={() => set('permissions', togglePermission(form.permissions, p.key))}
                          />
                          <span>
                            {p.label}
                            <span className="ml-1.5 font-mono text-label text-ink-faint">{p.key}</span>
                          </span>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              </fieldset>
            )
          })}
        </div>
      </section>

      {form.kind === 'AGENT' ? (
        <section className="flex flex-col gap-2" aria-label={t('access.roles.fields.scope')}>
          <div>
            <p className="text-meta font-medium text-ink">{t('access.roles.fields.scope')}</p>
            <p className="text-label text-ink-muted">{t('access.roles.fields.scopeHelp')}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <ScopeList
              title={t('access.roles.fields.products')}
              allLabel={t('access.roles.fields.allProducts')}
              options={(products.data ?? []).map((p) => ({ id: p.id, name: p.name }))}
              value={form.productIds}
              disabled={readOnly}
              onChange={(v) => set('productIds', v)}
            />
            <ScopeList
              title={t('access.roles.fields.customers')}
              allLabel={t('access.roles.fields.allCustomers')}
              options={(customers.data ?? []).map((c) => ({ id: c.id, name: c.name }))}
              value={form.customerIds}
              disabled={readOnly}
              onChange={(v) => set('customerIds', v)}
            />
          </div>
        </section>
      ) : null}

      <FormError error={error} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        {readOnly ? null : (
          <Button type="submit" disabled={pending}>
            {editing ? t('access.roles.save') : t('access.roles.create')}
          </Button>
        )}
      </div>
    </form>
  )
}

/** A group's select-all box; `some` shows as indeterminate. */
function GroupCheckbox({
  label,
  ariaLabel,
  state,
  disabled,
  onChange,
}: {
  label: string
  ariaLabel: string
  state: 'all' | 'some' | 'none'
  disabled: boolean
  onChange: (on: boolean) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some'
  }, [state])
  return (
    <label className="flex items-center gap-2 text-eyebrow font-medium uppercase text-ink-muted">
      <input ref={ref} type="checkbox" aria-label={ariaLabel} className="size-4 accent-primary" checked={state === 'all'} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

/** A checkbox list where nothing ticked means "all". */
function ScopeList({
  title,
  allLabel,
  options,
  value,
  disabled,
  onChange,
}: {
  title: string
  allLabel: string
  options: { id: string; name: string }[]
  value: string[]
  disabled: boolean
  onChange: (value: string[]) => void
}) {
  return (
    <fieldset className="rounded-lg border border-line p-3">
      <legend className="px-1 text-eyebrow font-medium uppercase text-ink-muted">{title}</legend>
      <p className={cn('pb-1.5 text-label', value.length === 0 ? 'font-medium text-primary' : 'text-ink-muted')}>{value.length === 0 ? allLabel : `${value.length} / ${options.length}`}</p>
      <ul className="flex max-h-40 flex-col gap-1.5 overflow-y-auto">
        {options.map((o) => (
          <li key={o.id}>
            <label className="flex items-center gap-2 text-meta text-ink-body">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={value.includes(o.id)}
                disabled={disabled}
                onChange={() => onChange(value.includes(o.id) ? value.filter((v) => v !== o.id) : [...value, o.id])}
              />
              {o.name}
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  )
}
